<template>
  <div class="column">
    <div class="card-interactive-header">
      <TextBase v-if="fullscreen" :text="title" :type="titleTypeFullscreen" weight="bold" />
      <TextBase v-else :text="title" :type="titleType" weight="bold" />
      <div class="card-interactive-controls" :class="{ fullscreen }">
        <template v-if="showControls">
          <slot name="controls"></slot>
          <div class="vertical-divider"></div>
        </template>
        <div class="row">
          <InfoButton
            v-if="showInfo"
            :state="false"
            class="icon"
            :class="{ fullscreen }"
            @click="onToggleInfoClick"
          />
          <ButtonFullscreen
            v-if="enableFullscreen"
            class="icon"
            :class="{ fullscreen }"
            :fullscreen="fullscreen"
            @toggleFullscreen="onToggleFullscreenClick"
          />
        </div>
      </div>
    </div>
    <slot></slot>
  </div>
</template>
<script lang="ts" setup>
import TextBase, { TextType } from '@/design-system/atoms/TextBase.vue'
import InfoButton from './InfoButton.vue'
import ButtonFullscreen from '@/design-system/atoms/buttons/ButtonFullscreen.vue'

type Props = {
  title: string
  titleType?: TextType
  titleTypeFullscreen?: TextType
  showInfo?: boolean
  showControls?: boolean
  enableFullscreen?: boolean
  fullscreen?: boolean
}

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'toggleShowInfo'): void
}

withDefaults(defineProps<Props>(), {
  titleType: 'paragraph',
  titleTypeFullscreen: 'h4',
  enableFullscreen: false,
  showInfo: false,
  showControls: false,
  fullscreen: false
})
const emit = defineEmits<Emits>()

function onToggleInfoClick() {
  emit('toggleShowInfo')
}

function onToggleFullscreenClick() {
  emit('toggleFullscreen')
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';

.column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.card-interactive-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.icon {
  width: 16px;
  height: 16px;
  color: variables.$color-greyscale-secondary;
  &.fullscreen {
    width: 24px;
    height: 24px;
  }
}
.card-interactive-footer.fullscreen {
  display: flex;
  flex-direction: column;
}
.card-interactive-footer {
  display: flex;
  flex-direction: column;
}
.card-interactive-footer-content {
  display: flex;
  justify-content: flex-end;
}
.card-interactive-footer-controller {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}
.card-interactive-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: variables.$gap-medium;
  &.fullscreen {
    gap: 24px;
  }
}

svg {
  color: variables.$color-greyscale-secondary;
}
.card-interactive {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  &.fullscreen {
    position: fixed;
    width: 90%;
    max-width: 1200px;
    height: auto;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    border: 1px;
    z-index: 2;
    gap: 16px;
    padding: 24px;
  }
}

.content-container {
  height: 100%;
}
.info-container {
  @include font-mixins.desktop-regular-label;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  @include mixins.scrollbar-vertical;
}
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.overlay {
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1;
}

.placeholder {
  width: 240px;
  height: 24px;
  background-color: bisque;
}

.vertical-divider {
  width: 1px;
  height: 24px;
  background-color: #e0e0e0;
}

.placeholder-card {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
}
</style>
